import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { playKitchenAlertSound } from '../../shared/audio/kitchenAlertSound';
import { getCounterReadySoundEnabled, setCounterReadySoundEnabled } from '../../shared/localPrefs';
import { showSuccess } from '../../shared/ui/appToast';
import type { KitchenTicket } from '../domain/hospitalityTypes';
import { kitchenTicketPrimaryRef } from '../domain/kitchenTicketPresentation';
import { useKitchenTicketCount } from './KitchenTicketCountProvider';
import { shouldSuppressCounterReadyAlert } from '../domain/kitchenScreenFocus';

const READY_WINDOW_MS = 30 * 60 * 1000;

export type ReadyForPickupTicket = {
  id: string;
  refLabel: string;
  readyAtMs: number;
};

type KitchenReadyAlertContextValue = {
  readyForPickup: ReadyForPickupTicket[];
  dismissReady: (ticketId: string) => void;
  counterReadySound: boolean;
  setCounterReadySound: (enabled: boolean) => void;
};

const KitchenReadyAlertContext = createContext<KitchenReadyAlertContextValue>({
  readyForPickup: [],
  dismissReady: () => undefined,
  counterReadySound: true,
  setCounterReadySound: () => undefined,
});

function pruneReady(list: ReadyForPickupTicket[], nowMs: number): ReadyForPickupTicket[] {
  return list.filter((t) => nowMs - t.readyAtMs <= READY_WINDOW_MS);
}

/**
 * Alertas "listo para retirar". Consume el snapshot compartido de
 * KitchenTicketCountProvider (1 fetch / 1 canal realtime por sucursal).
 */
export function KitchenReadyAlertProvider({ children }: { children: React.ReactNode }) {
  const branchId = useSessionStore((s) => s.currentBranchId);
  const hasKitchen = useHasBusinessCapability('kitchen_tickets');
  const canViewPos = useModulePermission('POS', 'view');
  const canViewOrders = useModulePermission('Orders', 'view');
  const shouldNotify = hasKitchen && (canViewPos || canViewOrders);
  const { snapshot } = useKitchenTicketCount();

  const statusByIdRef = useRef<Map<string, KitchenTicket['status']>>(new Map());
  const [readyForPickup, setReadyForPickup] = useState<ReadyForPickupTicket[]>([]);
  const [counterReadySound, setCounterReadySoundState] = useState(true);

  useEffect(() => {
    void getCounterReadySoundEnabled().then((v) => setCounterReadySoundState(v === '1'));
  }, []);

  const setCounterReadySound = useCallback((enabled: boolean) => {
    setCounterReadySoundState(enabled);
    void setCounterReadySoundEnabled(enabled ? '1' : '0');
  }, []);

  const dismissReady = useCallback((ticketId: string) => {
    setReadyForPickup((prev) => prev.filter((t) => t.id !== ticketId));
  }, []);

  // Reset del diff cuando cambia sucursal o se apagan los permisos.
  useEffect(() => {
    statusByIdRef.current = new Map();
    setReadyForPickup([]);
  }, [branchId, shouldNotify]);

  useEffect(() => {
    if (!snapshot || !branchId || !shouldNotify) return;
    const nowMs = Date.now();
    const prev = statusByIdRef.current;
    const nextMap = new Map<string, KitchenTicket['status']>();

    for (const ticket of snapshot.kitchenTickets) {
      nextMap.set(ticket.id, ticket.status);
      const was = prev.get(ticket.id);
      if (
        ticket.status === 'ready' &&
        was != null &&
        was !== 'ready' &&
        was !== 'served' &&
        !shouldSuppressCounterReadyAlert(ticket.id)
      ) {
        const refLabel = kitchenTicketPrimaryRef(ticket);
        if (ticket.diningTableId) {
          const rawName = snapshot.diningTables
            .find((t) => t.id === ticket.diningTableId)
            ?.name?.trim();
          const mesaLabel = rawName
            ? /^mesa\b/i.test(rawName)
              ? rawName
              : `Mesa ${rawName}`
            : 'Mesa';
          showSuccess('Listo en mesa', `${mesaLabel} — ${refLabel}`);
        } else {
          showSuccess('Listo en mostrador', `Pedido ${refLabel} — retirá en mostrador`);
        }
        void getCounterReadySoundEnabled().then((v) => {
          if (v !== '0') playKitchenAlertSound();
        });
        setReadyForPickup((list) =>
          pruneReady(
            [{ id: ticket.id, refLabel, readyAtMs: nowMs }, ...list.filter((x) => x.id !== ticket.id)],
            nowMs,
          ),
        );
      }
    }

    statusByIdRef.current = nextMap;

    setReadyForPickup((list) => {
      const stillReady = new Set(
        snapshot.kitchenTickets.filter((t) => t.status === 'ready').map((t) => t.id),
      );
      return pruneReady(
        list.filter((t) => stillReady.has(t.id)),
        nowMs,
      );
    });
  }, [snapshot, branchId, shouldNotify]);

  const value = useMemo(
    () => ({ readyForPickup, dismissReady, counterReadySound, setCounterReadySound }),
    [readyForPickup, dismissReady, counterReadySound, setCounterReadySound],
  );

  return (
    <KitchenReadyAlertContext.Provider value={value}>{children}</KitchenReadyAlertContext.Provider>
  );
}

export function useKitchenReadyAlerts(): KitchenReadyAlertContextValue {
  return useContext(KitchenReadyAlertContext);
}
